import { Figure } from "@/actors/figure";

export class FigureManager {
  private figures: any;

  constructor() {
    this.figures = new Map();
  }

  /**
   * Adds a new figure to the location.
   * @param figure - The figure to add.
   * @returns The ID assigned to the figure.
   */
  public addFigure(data: any) {
    const figureExists = this.getFigureById(data.id);
    if (figureExists) return;
    
    const figure = new Figure({
      look: data.look,
      clientId: data.id,
      username: data.username,
      playerPosition: data.position,
      cameraPosition: data.tilemapPosition,
      direction: data.direction,
    }); 
    
    // Create the figure
    const figureData = {
      figure,
      ...data,
    };

    this.figures.set(data.id, figureData);
    return this.figures.get(data.id);
  }

  /**
   * Retrieves a figure by its ID.
   * @param id - The ID of the figure.
   * @returns The figure, or undefined if not found.
   */
  public getFigureById(id: string): Figure | undefined {
    return this.figures.get(id);
  }

  /**
   * Removes a figure by its ID.
   * @param id - The ID of the figure to remove.
   * @returns True if the figure was removed, false if not found.
   */
  public removeFigureById(id: string): boolean {
    return this.figures.delete(id);
  }

  /**
   * Retrieves all figures in the location.
   * @returns An array of all figures.
   */
  public getAllFigures(): Figure[] {
    return Array.from(this.figures.values());
  }

  /**
   * Retrieves the currently active player.
   * @returns The active player's figure, or undefined if no player is active.
   */
  public getActivePlayer(): Figure | undefined {
    for (const figureData of this.figures.values()) {
      if (figureData.isMe) {
        return figureData.figure;
      }
    }
    return undefined; // Return undefined if no current player is found
  }

  /**
   * Retrieves all new figures.
   * New figures are defined based on a custom condition, e.g., a `isNew` flag.
   * @returns An array of new figures.
   */
  public getNewFigures(): Figure[] {
    return Array.from(this.figures.values())
      .filter((figureData: any) => figureData.figure.isNew) // Assuming `isNew` exists
      .map((figureData: any) => figureData.figure);
  }

  /**
   * Retrieves a figure by its position.
   * @param position - The position to search for.
   * @returns The figure at the specified position, or undefined if not found.
   */
  public getFigureByPosition(position: {
    x: number;
    y: number;
  }): Figure | undefined {
    for (const figureData of this.figures.values()) {
      if (
        figureData.figure.x === position.x &&
        figureData.figure.y === position.y
      ) {
        return figureData.figure;
      }
    }
    return undefined; // Return undefined if no figure is found at the position
  }
}
